/* eslint-disable react/prop-types */
import CmdKGroup from 'src/components/Cmdk/CmdKGroup'
import CommandItem from 'src/components/Cmdk/CmdKItem'
import UploadFileRoundedIcon from '@mui/icons-material/UploadFileRounded'
import AddCircleRoundedIcon from '@mui/icons-material/AddCircleRounded'
import FilterListRoundedIcon from '@mui/icons-material/FilterListRounded'
import ToggleOnIcon from '@mui/icons-material/ToggleOn'
import ToggleOffIcon from '@mui/icons-material/ToggleOffOutlined'
import { filter } from 'src/components/Cmdk/filter'
import type { CommandMenuGroup } from 'src/components/Cmdk/Cmdk'
import CmdkSubMenu from 'src/components/Cmdk/CmdkSubMenu'
import type { CmdkSubMenuItem } from 'src/components/Cmdk/CmdkSubMenu'
import ProductTypeIcon from 'src/components/ProductTypeIcon/ProductTypeIcon'
import FilterListOffRoundedIcon from '@mui/icons-material/FilterListOffRounded'

export interface CmdkProductListPageModuleProps
  extends Record<string, unknown> {
  openIngestInventoryModal?: () => void
  openCreateProductDrawer?: () => void
  showLowStock?: boolean
  setSelectedType?: (type: string) => void
  setShowLowStock?: (showLowStock: boolean) => void
  onClose?: () => void
  onNavigate?: (groups: CommandMenuGroup[]) => void
  onBack?: () => void
  pages?: CommandMenuGroup[][]
  search?: string
}

const CmdkProductListPage: React.FC<CmdkProductListPageModuleProps> = ({
  openIngestInventoryModal,
  openCreateProductDrawer,
  showLowStock,
  setSelectedType,
  setShowLowStock,
  onNavigate,
  onBack,
  pages,
  onClose,
  search,
}) => {
  const topLevel = (pages?.length ?? 0) === 0
  const items = [
    {
      label: 'Create Product',
      startIcon: <AddCircleRoundedIcon />,
      keywords: ['product', 'new', 'create'],
      onSelect: () => {
        openCreateProductDrawer?.()
        onClose?.()
      },
    },
    {
      label: 'Import Inventory Spreadsheet',
      startIcon: <UploadFileRoundedIcon />,
      keywords: ['import', 'inventory', 'spreadsheet', 'upload'],
      onSelect: () => {
        openIngestInventoryModal?.()
        onClose?.()
      },
    },
    {
      label: showLowStock ? 'Toggle Low Stock off' : 'Toggle Low Stock on',
      startIcon: showLowStock ? <ToggleOffIcon /> : <ToggleOnIcon />,
      keywords: ['low', 'stock', 'show', 'hide', 'on', 'off'],
      onSelect: () => {
        setShowLowStock?.(!showLowStock)
        onClose?.()
      },
    },
  ]

  const filteredItems = items.filter((item) =>
    filter(item.label, search, item.keywords)
  )

  // Submenu items used for filtering visibility
  const submenuItems: CmdkSubMenuItem[] = [
    {
      label: 'Filter All',
      keywords: ['all', 'filter'],
      startIcon: <FilterListOffRoundedIcon />,
      onSelect: () => {
        setSelectedType?.('')
        onClose?.()
      },
    },
    {
      label: 'Filter Rotable',
      keywords: ['rotable', 'filter'],
      startIcon: <ProductTypeIcon variant="ROTABLE" />,
      onSelect: () => {
        setSelectedType?.('ROTABLE')
        onClose?.()
      },
    },
    {
      label: 'Filter Tools',
      keywords: ['tools', 'filter'],
      startIcon: <ProductTypeIcon variant="TOOL" />,
      onSelect: () => {
        setSelectedType?.('TOOL')
        onClose?.()
      },
    },
    {
      label: 'Filter Consumables',
      keywords: ['consumables', 'filter'],
      startIcon: <ProductTypeIcon variant="CONSUMABLE" />,
      onSelect: () => {
        setSelectedType?.('CONSUMABLE')
        onClose?.()
      },
    },
    {
      label: 'Filter Other',
      keywords: ['other', 'filter'],
      startIcon: <ProductTypeIcon variant="OTHER" />,
      onSelect: () => {
        setSelectedType?.('OTHER')
        onClose?.()
      },
    },
  ]

  const submenuMatches = submenuItems.filter((item) =>
    filter(item.label, search, item.keywords)
  )

  if (filteredItems.length === 0 && submenuMatches.length === 0) return null

  return (
    <CmdKGroup heading={topLevel ? 'Product Page' : ''}>
      <CmdkSubMenu
        pages={pages || []}
        parentStartIcon={<FilterListRoundedIcon />}
        parentLabel={topLevel ? 'Filter Products...' : ''}
        onNavigate={onNavigate}
        search={search || ''}
        items={submenuItems}
        onClose={onClose}
        onBack={onBack}
      />
      {topLevel &&
        filteredItems.map((item) => (
          <CommandItem
            key={item.label}
            startIcon={item.startIcon}
            onSelect={item.onSelect}
            keywords={item.keywords}
          >
            {item.label}
          </CommandItem>
        ))}
    </CmdKGroup>
  )
}

export default CmdkProductListPage
